import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Sparkles, ArrowRight, Code2, Rocket, Globe } from 'lucide-react';

const Landing = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const features = [
        {
            icon: <Sparkles className="w-6 h-6" />,
            title: "Multi-Model AI",
            description: "Switch between Claude, GPT, Kimi, and GLM to find the model that best fits your task and budget."
        },
        {
            icon: <Code2 className="w-6 h-6" />,
            title: "Live Preview",
            description: "Watch your code come to life in real time — every change renders instantly in an interactive preview."
        },
        {
            icon: <Rocket className="w-6 h-6" />,
            title: "One-Click Deploy",
            description: "Ship your prototype to AWS EC2 in a single click, with no DevOps or server configuration."
        },
        {
            icon: <Globe className="w-6 h-6" />,
            title: "Demo Ready",
            description: "Build polished web application prototypes with React and modern tooling, ready to share and demo."
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Navbar */}
            <nav className="bg-white border-b border-gray-200 px-6 py-3 sticky top-0 z-50">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <img 
                            src="https://blog.mersi.ai/wp-content/uploads/2025/05/mersi.ai-logo-1024px.jpg" 
                            alt="Mersi.ai Logo" 
                            className="w-9 h-9 rounded object-cover"
                        />
                        <span className="font-heading text-lg text-gray-900">VibeCoder</span>
                    </Link>
                    
                    <div className="flex items-center gap-3">
                        {user ? (
                            <Button 
                                onClick={() => navigate('/dashboard')}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid="go-to-dashboard-btn"
                            >
                                Go to Dashboard
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        ) : (
                            <>
                                <Button 
                                    variant="ghost" 
                                    onClick={() => navigate('/login')}
                                    className="text-gray-600 hover:text-gray-900"
                                    data-testid="login-btn"
                                >
                                    Sign In
                                </Button>
                                <Button 
                                    onClick={() => navigate('/signup')}
                                    className="bg-blue-600 hover:bg-blue-700"
                                    data-testid="signup-btn"
                                >
                                    Get Started
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="px-6 py-16 md:py-24">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 mb-6">
                        <Sparkles className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-700">AI-Powered Development</span>
                    </div>
                    
                    <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-gray-900 mb-6">
                        Build Apps with
                        <span className="text-blue-600"> AI Magic</span>
                    </h1>
                    
                    <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                        Transform your ideas into web applications demo prototypes. 
                        Just describe what you want, and let our AI do the coding.
                    </p>

                    <p className="text-base text-gray-500 mb-10 max-w-2xl mx-auto">
                        VibeCoder is your AI coding partner that turns plain language into working
                        software. Skip the boilerplate, environment setup, and configuration — go
                        from a single sentence to a running web app prototype in minutes. Whether
                        you&apos;re validating a startup idea, building an internal tool, or learning to
                        code, VibeCoder does the heavy lifting so you can focus on your idea.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Button 
                            size="lg" 
                            onClick={() => navigate(user ? '/dashboard' : '/signup')}
                            className="bg-blue-600 hover:bg-blue-700 text-base px-8 py-5"
                            data-testid="hero-cta-btn"
                        >
                            Start Building Free
                            <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                        <Button 
                            size="lg" 
                            variant="outline"
                            onClick={() => window.open('https://mersi.ai', '_blank')}
                            className="text-base px-8 py-5 border-gray-300"
                            data-testid="hero-demo-btn"
                        >
                            About us
                        </Button>
                    </div>

                    {/* Preview */}
                    <div className="mt-16 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
                        <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 border-b border-gray-200">
                            <div className="flex gap-1.5">
                                <div className="w-3 h-3 rounded-full bg-red-400" />
                                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                                <div className="w-3 h-3 rounded-full bg-green-400" />
                            </div>
                            <span className="text-xs text-gray-500 ml-2">VibeCoder Editor</span>
                        </div>
                        <div className="p-8 h-64 flex items-center justify-center bg-gray-50">
                            <div className="text-center">
                                <Code2 className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                                <p className="text-gray-500">Your AI-powered IDE awaits...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="px-6 py-16 bg-white border-t border-gray-200" data-testid="how-it-works-section">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="font-heading text-2xl md:text-3xl text-gray-900 mb-3">
                            From prompt to prototype in three steps
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Building with VibeCoder is simple. No setup, no install — just your
                            browser and your idea.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                step: "1",
                                title: "Describe",
                                description: "Tell VibeCoder what you want to build in plain English. No technical spec or wireframe required."
                            },
                            {
                                step: "2",
                                title: "Generate & preview",
                                description: "Pick your AI model and watch it write clean React code, then see your app render live in an in-browser preview you can interact with immediately."
                            },
                            {
                                step: "3",
                                title: "Refine & deploy",
                                description: "Iterate with follow-up prompts, edit the code directly when you want fine-grained control, and deploy to AWS EC2 with a single click."
                            }
                        ].map((item) => (
                            <div key={item.step} className="text-center" data-testid={`how-step-${item.step}`}>
                                <div className="w-12 h-12 rounded-full bg-blue-600 text-white font-heading text-lg flex items-center justify-center mx-auto mb-4">
                                    {item.step}
                                </div>
                                <h3 className="font-heading text-lg text-gray-900 mb-2">{item.title}</h3>
                                <p className="text-gray-600 text-sm">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="px-6 py-16 bg-white border-t border-gray-200">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="font-heading text-2xl md:text-3xl text-gray-900 mb-3">
                            Everything you need to ship fast
                        </h2>
                        <p className="text-gray-600">
                            From idea to prototype in minutes, not months.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, index) => (
                            <Card key={index} className="bg-white border-gray-200 hover:border-blue-300 transition-colors">
                                <CardHeader>
                                    <div className="w-10 h-10 rounded bg-blue-50 flex items-center justify-center text-blue-600 mb-3">
                                        {feature.icon}
                                    </div>
                                    <CardTitle className="font-heading text-lg text-gray-900">{feature.title}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <CardDescription className="text-gray-600">
                                        {feature.description}
                                    </CardDescription>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="px-6 py-16">
                <div className="max-w-3xl mx-auto">
                    <div className="bg-blue-600 rounded-lg p-10 text-center text-white">
                        <h2 className="font-heading text-2xl md:text-3xl mb-3">
                            Ready to build something amazing?
                        </h2>
                        <p className="text-blue-100 mb-8">
                            Stop staring at a blank editor. Describe your idea, watch VibeCoder
                            build it before your eyes, and launch your prototype today — start free,
                            no credit card required.
                        </p>
                        <Button 
                            size="lg"
                            onClick={() => navigate(user ? '/dashboard' : '/signup')}
                            className="bg-white text-blue-600 hover:bg-blue-50"
                            data-testid="cta-start-btn"
                        >
                            Start Building Now
                            <Sparkles className="w-5 h-5 ml-2" />
                        </Button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-gray-200 px-6 py-6 bg-white">
                <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <img 
                            src="https://blog.mersi.ai/wp-content/uploads/2025/05/mersi.ai-logo-1024px.jpg" 
                            alt="Mersi.ai Logo" 
                            className="w-5 h-5 rounded object-cover"
                        />
                        <span className="font-heading text-gray-900">VibeCoder</span>
                    </div>
                    <p className="text-sm text-gray-500">
                        © 2025 VibeCoder by Mersi.ai
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default Landing;
